import { appData, tempConfigData } from '../../app/state/state.js';
import {
    migrateData,
    triggerNonAdminLogoutWave,
} from './settingsApi.js';
import {
    deleteBrandAsset,
    uploadBrandAsset,
} from '../../shared/api/brandAssets.js';
import { openConfirmationModal } from '../../shared/ui/confirmationModal.js';
import {
    bindStatusPrioritySettingsControls,
    renderOptionEditor,
} from './statusPrioritySettings.js';
import { createSelectControl } from '../../shared/ui/controls/selectControl.js';
import { createDomainOptions } from '../../shared/ui/controls/controlOptions.js';
import { escapeHTML } from '../../shared/utils/contentUtils.js';
import {
    DEFAULT_BRANDING,
    applyBrandingVariables,
    clampSurfaceTint,
    getBrandingTokens,
    getTintedSurface,
    normalizeHexColor,
    SURFACE_TINT_MAX_OPACITY,
    SURFACE_TINT_MIN_OPACITY,
} from '../../shared/ui/brandingTokens.js';

let updateSaveButtonHandler = () => {};
let migrationSelection = {
    newVal: "",
    oldVal: "",
    type: "task",
};

const MIGRATION_TYPE_OPTIONS = Object.freeze([
    { value: "task", label: "Task Status", group: "Statuses" },
    { value: "patient", label: "Patient Status", group: "Statuses" },
    { value: "lab", label: "Lab Status", group: "Statuses" },
    { value: "project", label: "Project Status", group: "Statuses" },
    { value: "asset", label: "Asset Status", group: "Statuses" },
    { value: "asset_issue", label: "Asset Issue Status", group: "Statuses" },
    { value: "maintenance", label: "Maintenance Status", group: "Statuses" },
    { value: "task_type", label: "Task Types", group: "Types" },
    { value: "patient_type", label: "Patient Types", group: "Types" },
    { value: "lab_type", label: "Lab Types", group: "Types" },
    { value: "project_type", label: "Project Types", group: "Types" },
    { value: "asset_type", label: "Asset Types", group: "Types" },
    { value: "maintenance_type", label: "Maintenance Types", group: "Types" },
    { value: "global_urgency", label: "Urgency", group: "Global" },
]);

const BRANDING_COLOR_CONTROLS = Object.freeze([
    {
        key: "primaryColor",
        label: "Primary colour",
        description: "Used for primary buttons, toggles, active controls, and the main app brand highlight.",
    },
    {
        key: "primaryHoverColor",
        label: "Primary hover colour",
        description: "Used when primary brand actions are hovered, pressed, or otherwise active.",
    },
    {
        key: "accentColor",
        label: "Accent colour",
        description: "Used for active tabs, selected states, notification badges, and app-owned accents.",
    },
    {
        key: "secondaryAccentColor",
        label: "Secondary accent colour",
        description: "Used for supporting highlights where a second brand accent is needed.",
    },
]);

const BRANDING_ASSET_CONTROLS = Object.freeze([
    {
        key: "logo",
        label: "Logo",
        description: "Used in app and on print items.",
        requirements: "PNG, JPG, or WebP. Max 5 MB. Recommended: wide logo, about 600 x 200 px.",
        previewClass: "app-brand-asset-preview--logo",
        emptyIcon: "image",
    },
    {
        key: "favicon",
        label: "Favicon",
        description: "Used for the browser tab icon after the app loads.",
        requirements: "PNG, JPG, or WebP. Max 5 MB. Recommended: square 32 x 32 or 48 x 48 px.",
        previewClass: "app-brand-asset-preview--favicon",
        emptyIcon: "web_asset",
    },
]);

/**
 * Registers the save-button dirty state callback for general settings.
 *
 * @param {Function} handler Dirty-state callback.
 * @returns {void}
 */
export function setGeneralSettingsUpdateSaveButton(handler) {
    updateSaveButtonHandler = handler || (() => {});
}

function attrValue(value) {
    return escapeHTML(String(value ?? "")).replaceAll('"', "&quot;");
}

function ensureBrandingDraft() {
    const branding = getBrandingTokens(tempConfigData || {});
    tempConfigData.branding = {
        ...(tempConfigData.branding || {}),
        ...branding,
    };
    return tempConfigData.branding;
}

function isHexColorInput(value) {
    return /^#[0-9a-f]{6}$/i.test(String(value || "").trim()) ||
        /^#[0-9a-f]{3}$/i.test(String(value || "").trim());
}

function syncBrandingControl(key, value) {
    const controls = document.querySelectorAll(`[data-branding-key="${key}"]`);
    controls.forEach((control) => {
        if (key === "surfaceTintOpacity") {
            const strength = getSurfaceTintStrength(value);
            control.value = String(strength);
            if (control.type === "range") {
                control.style.setProperty("--app-colour-tint-progress", `${strength}%`);
            }
            return;
        }
        if (control.type === "color") {
            control.value = value;
            return;
        }
        control.value = value;
    });

    const swatch = document.querySelector(`[data-branding-swatch="${key}"]`);
    if (swatch) {
        swatch.style.backgroundColor = value;
    }
}

function syncBrandingPreview() {
    const branding = getBrandingTokens({branding: tempConfigData.branding});
    const content = document.getElementById("settings-content");
    if (content) {applyBrandingVariables(content, {branding});}

    const preview = document.querySelector("[data-branding-tint-preview]");
    if (preview) {
        preview.style.backgroundColor = getTintedSurface(
            branding.primaryColor,
            branding.surfaceTintOpacity,
        );
    }
}

function syncRuntimeBrandAsset(key, asset) {
    ensureBrandingDraft()[key] = asset || null;
    if (!appData.config) {return;}
    appData.config.branding = {
        ...(appData.config.branding || {}),
        [key]: asset || null,
    };
}

function setBrandAssetProgress(key, message = "") {
    const progress = document.querySelector(
        `[data-branding-asset-progress="${key}"]`,
    );
    if (!progress) {return;}
    progress.textContent = message;
    progress.classList.toggle("hidden", !message);
}

async function handleBrandAssetUpload(target, rerender) {
    const key = target.dataset.brandingAssetInput;
    const file = target.files?.[0] || null;
    if (!file) {return;}

    target.disabled = true;
    setBrandAssetProgress(key, "Uploading...");
    try {
        const asset = await uploadBrandAsset(key, file, {
            onProgress: (progress) => {
                setBrandAssetProgress(key, `Uploading ${Math.round(progress)}%`);
            },
        });
        syncRuntimeBrandAsset(key, asset);
        setBrandAssetProgress(key, "");
        rerender?.();
    } catch (error) {
        console.error("Failed to upload brand asset:", error);
        setBrandAssetProgress(key, "");
        openConfirmationModal(
            "Upload Failed",
            error.message || "Could not upload the brand asset.",
            () => {},
            false,
            "OK",
        );
    } finally {
        target.value = "";
        target.disabled = false;
    }
}

async function handleBrandAssetDelete(key, rerender) {
    setBrandAssetProgress(key, "Deleting...");
    try {
        await deleteBrandAsset(key);
        syncRuntimeBrandAsset(key, null);
        setBrandAssetProgress(key, "");
        rerender?.();
    } catch (error) {
        console.error("Failed to delete brand asset:", error);
        setBrandAssetProgress(key, "");
        openConfirmationModal(
            "Delete Failed",
            error.message || "Could not delete the brand asset.",
            () => {},
            false,
            "OK",
        );
    }
}

function getSurfaceTintStrength(value) {
    const safeValue = clampSurfaceTint(value);
    const range = SURFACE_TINT_MAX_OPACITY - SURFACE_TINT_MIN_OPACITY;
    return Math.round(((safeValue - SURFACE_TINT_MIN_OPACITY) / range) * 100);
}

function getSurfaceTintFromStrength(value) {
    const strength = Math.min(100, Math.max(0, Number(value)));
    const range = SURFACE_TINT_MAX_OPACITY - SURFACE_TINT_MIN_OPACITY;
    return clampSurfaceTint(
        SURFACE_TINT_MIN_OPACITY + ((strength / 100) * range),
    );
}

function renderBrandColourControl(control, branding) {
    const value = branding[control.key];
    const inputId = `branding-${control.key}`;
    return `
        <div class="app-colour-row">
            <label class="app-colour-swatch-control" for="${inputId}">
                <span
                    class="app-colour-swatch"
                    style="background-color: ${attrValue(value)}"
                    data-branding-swatch="${attrValue(control.key)}"
                    aria-hidden="true"
                ></span>
                <input
                    id="${inputId}"
                    type="color"
                    class="app-colour-picker"
                    value="${attrValue(value)}"
                    data-branding-key="${attrValue(control.key)}"
                    data-branding-control="picker"
                    aria-label="Pick ${attrValue(control.label)}"
                >
            </label>
            <div class="app-colour-copy">
                <div class="app-colour-label">${escapeHTML(control.label)}</div>
                <p class="app-colour-description">${escapeHTML(control.description)}</p>
            </div>
            <input
                type="text"
                class="app-colour-hex-input"
                value="${attrValue(value)}"
                maxlength="7"
                spellcheck="false"
                data-branding-key="${attrValue(control.key)}"
                data-branding-control="text"
                aria-label="${attrValue(control.label)} hex value"
            >
        </div>
    `;
}

function getBrandAssetMeta(asset) {
    if (!asset?.url) {return "";}
    const dimensions = asset.width && asset.height ?
        `${asset.width} x ${asset.height}px` :
        "Dimensions unavailable";
    const size = Number.isFinite(Number(asset.size)) ?
        `${(Number(asset.size) / 1024).toFixed(0)} KB` :
        "Size unavailable";
    const type = asset.type || "Image";
    return `${dimensions} - ${size} - ${type}`;
}

function renderBrandAssetControl(control, branding) {
    const asset = branding?.[control.key] || null;
    const inputId = `branding-${control.key}-upload`;
    const hasAsset = Boolean(asset?.url);
    return `
        <div class="app-brand-asset-row" data-branding-asset-row="${attrValue(control.key)}">
            <div class="app-brand-asset-preview ${control.previewClass}">
                ${hasAsset ? `
                    <img src="${attrValue(asset.url)}" alt="${attrValue(control.label)} preview">
                ` : `
                    <span class="material-symbols-outlined" aria-hidden="true">${control.emptyIcon}</span>
                `}
            </div>
            <div class="app-brand-asset-copy">
                <div class="app-colour-label">${escapeHTML(control.label)}</div>
                <p class="app-colour-description">${escapeHTML(control.description)}</p>
                <p class="app-brand-asset-requirements">${escapeHTML(control.requirements)}</p>
                <p class="app-brand-asset-meta" data-branding-asset-meta="${attrValue(control.key)}">
                    ${hasAsset ? escapeHTML(getBrandAssetMeta(asset)) : "No custom asset uploaded."}
                </p>
                <div
                    class="app-brand-asset-progress hidden"
                    data-branding-asset-progress="${attrValue(control.key)}"
                    aria-live="polite"
                ></div>
            </div>
            <div class="app-brand-asset-actions">
                <input
                    id="${inputId}"
                    type="file"
                    class="sr-only"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    data-branding-asset-input="${attrValue(control.key)}"
                >
                <label class="app-brand-asset-btn" for="${inputId}">
                    ${hasAsset ? "Replace" : "Upload"}
                </label>
                <button
                    type="button"
                    class="app-brand-asset-delete"
                    data-branding-asset-delete="${attrValue(control.key)}"
                    ${hasAsset ? "" : "disabled"}
                >
                    Delete
                </button>
            </div>
        </div>
    `;
}

function renderAppColoursSection() {
    const branding = ensureBrandingDraft();
    const tintPreview = getTintedSurface(
        branding.primaryColor,
        branding.surfaceTintOpacity,
    );
    const tintStrength = getSurfaceTintStrength(branding.surfaceTintOpacity);
    return `
        <div class="bg-white p-6 rounded-lg shadow-md mb-8 max-w-5xl app-colours-panel">
            <h4 class="text-lg font-semibold mb-5">App Branding</h4>
            <div class="app-colours-section-heading">
                <h5>Colours</h5>
                <hr>
            </div>
            <div class="app-colours-list">
                ${BRANDING_COLOR_CONTROLS.map((control) => renderBrandColourControl(control, branding)).join("")}
                <div class="app-colour-row app-colour-row--opacity">
                    <span
                        class="app-colour-tint-preview"
                        style="background-color: ${attrValue(tintPreview)}"
                        data-branding-tint-preview
                        aria-hidden="true"
                    ></span>
                    <div class="app-colour-copy">
                        <div class="app-colour-label">Surface tint strength</div>
                        <p class="app-colour-description">Controls how visible pale brand-tinted surfaces are across the app.</p>
                    </div>
                    <div class="app-colour-opacity-controls">
                        <input
                            id="branding-surfaceTintOpacity"
                            type="range"
                            min="0"
                            max="100"
                            step="1"
                            value="${attrValue(tintStrength)}"
                            style="--app-colour-tint-progress: ${attrValue(tintStrength)}%;"
                            data-native-field-skip="true"
                            data-branding-key="surfaceTintOpacity"
                            data-branding-control="range"
                            aria-label="Surface tint strength"
                        >
                        <label class="app-colour-percent-field">
                            <input
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                value="${attrValue(tintStrength)}"
                                data-branding-key="surfaceTintOpacity"
                                data-branding-control="number"
                                aria-label="Surface tint strength percentage"
                            >
                            <span>%</span>
                        </label>
                    </div>
                </div>
            </div>
            <div class="app-colours-section-heading app-brand-assets-heading">
                <h5>Assets</h5>
                <hr>
            </div>
            <div class="app-brand-assets-list">
                ${BRANDING_ASSET_CONTROLS.map((control) => renderBrandAssetControl(control, branding)).join("")}
            </div>
        </div>
    `;
}

function getMigrationSourceOptions(type) {
    switch (type) {
        case 'task': return tempConfigData?.taskStatus || [];
        case 'patient': return tempConfigData?.patientStatus || [];
        case 'project': return tempConfigData?.projectStatus || [];
        case 'asset': return tempConfigData?.equipmentStatus || [];
        case 'asset_issue': return tempConfigData?.issueStatus || [];
        case 'lab': return tempConfigData?.labStatus || [];
        case 'maintenance': return tempConfigData?.maintenanceStatus || [];
        case 'global_urgency': return tempConfigData?.priorities || [];
        case 'task_type': return tempConfigData?.taskTypes || [];
        case 'patient_type': return tempConfigData?.patientTypes || [];
        case 'project_type': return tempConfigData?.projectTypes || [];
        case 'asset_type': return tempConfigData?.assetTypes || [];
        case 'lab_type': return tempConfigData?.labTypes || [];
        case 'maintenance_type': return tempConfigData?.maintenanceTypes || [];
        default: return [];
    }
}

function getMigrationValueOptions(type) {
    return createDomainOptions(getMigrationSourceOptions(type))
        .map((option) => ({ ...option, accentColor: "" }));
}

function findMigrationOptionLabel(options, value) {
    return options.find((option) => String(option.value) === String(value))?.label ||
        value ||
        "";
}

function getMigrationFieldLabel(type) {
    if (type === "global_urgency") {return "Urgency";}
    if (type.endsWith("_type")) {return "Type";}
    return "Status";
}

function getMigrationTargetLabel(type) {
    const typeLabelMap = {
        asset: "assets",
        asset_issue: "asset issues",
        asset_type: "asset types",
        global_urgency: "items",
        lab: "labs",
        lab_type: "lab types",
        maintenance: "maintenance items",
        maintenance_type: "maintenance types",
        patient: "patients",
        patient_type: "patient types",
        project: "projects",
        project_type: "project types",
        task: "tasks",
        task_type: "task types",
    };
    return typeLabelMap[type] || `${type}s`;
}

function normalizeMigrationSelections(options) {
    const optionValues = options.map((option) => String(option.value));
    if (!optionValues.includes(String(migrationSelection.oldVal))) {
        migrationSelection.oldVal = optionValues[0] || "";
    }
    if (!optionValues.includes(String(migrationSelection.newVal))) {
        migrationSelection.newVal = optionValues[0] || "";
    }
}

function mountMigrationValueControls(container) {
    const fromRoot = container.querySelector('#migrate-old-val-control');
    const toRoot = container.querySelector('#migrate-new-val-control');
    if (!fromRoot || !toRoot) {return;}

    const valueOptions = getMigrationValueOptions(migrationSelection.type);
    normalizeMigrationSelections(valueOptions);
    fromRoot.replaceChildren();
    toRoot.replaceChildren();

    createSelectControl({
        root: fromRoot,
        options: valueOptions,
        value: migrationSelection.oldVal,
        searchable: valueOptions.length >= 7,
        searchThreshold: 7,
        portalMenu: true,
        placeholder: "Select source value",
        emptyMessage: "No options found.",
        accessibleName: "From old value",
        mirror: false,
        onChange: (value) => {
            migrationSelection.oldVal = value;
        },
    });

    createSelectControl({
        root: toRoot,
        options: valueOptions,
        value: migrationSelection.newVal,
        searchable: valueOptions.length >= 7,
        searchThreshold: 7,
        portalMenu: true,
        placeholder: "Select target value",
        emptyMessage: "No options found.",
        accessibleName: "To new value",
        mirror: false,
        onChange: (value) => {
            migrationSelection.newVal = value;
        },
    });
}

function mountMigrationControls(container) {
    const typeRoot = container.querySelector('#migrate-type-control');
    if (!typeRoot) {return;}

    if (!MIGRATION_TYPE_OPTIONS.some((option) => option.value === migrationSelection.type)) {
        migrationSelection.type = "task";
    }

    createSelectControl({
        root: typeRoot,
        options: MIGRATION_TYPE_OPTIONS,
        value: migrationSelection.type,
        searchable: true,
        searchThreshold: 7,
        portalMenu: true,
        placeholder: "Select migration type",
        accessibleName: "Migration type",
        mirror: false,
        onChange: (value) => {
            migrationSelection = { newVal: "", oldVal: "", type: value };
            mountMigrationValueControls(container);
        },
    });

    mountMigrationValueControls(container);
}

function getMaintenanceLockState(configKey) {
    switch (configKey) {
        case "notificationFlowBlocked":
            return tempConfigData.notificationFlowBlocked ? "Blocked" : "Active";
        case "drugsUiLocked":
        case "nonAdminSignInLocked":
            return tempConfigData[configKey] ? "Locked" : "Unlocked";
        default:
            return "";
    }
}

function syncMaintenanceLockControl(configKey) {
    const input = document.querySelector(`[data-config-key="${configKey}"]`);
    if (input && input.type === "checkbox") {
        input.checked = tempConfigData[configKey] === true;
    }

    const stateLabel = document.querySelector(
        `[data-maintenance-lock-state="${configKey}"]`,
    );
    if (!stateLabel) {return;}

    const isLocked = tempConfigData[configKey] === true;
    stateLabel.textContent = getMaintenanceLockState(configKey);
    stateLabel.classList.toggle("text-amber-700", isLocked);
    stateLabel.classList.toggle("text-gray-500", !isLocked);
}

function syncMaintenanceLockControls() {
    [
        "notificationFlowBlocked",
        "drugsUiLocked",
        "nonAdminSignInLocked",
    ].forEach(syncMaintenanceLockControl);
}

/**
 * Renders the General settings tab.
 *
 * @param {HTMLElement} container The tab container element.
 * @returns {void}
 */
export function renderGeneralTab(container) {
    if (tempConfigData.notificationFlowBlocked !== true) {tempConfigData.notificationFlowBlocked = false;}
    if (tempConfigData.drugsUiLocked !== true) {tempConfigData.drugsUiLocked = false;}
    if (tempConfigData.nonAdminSignInLocked !== true) {tempConfigData.nonAdminSignInLocked = false;}

    container.innerHTML = `
        <div class="max-w-5xl">
            <div class="bg-white p-6 rounded-lg shadow-md mb-8">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Global Settings</h4>
                ${renderOptionEditor('Urgency Levels', 'priorities', tempConfigData?.priorities || [], true, null)}
            </div>
            ${renderAppColoursSection()}
            <div class="bg-white p-6 rounded-lg shadow-md mb-8">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Maintenance Locks</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="rounded-lg border border-gray-200 p-4"><div class="flex items-start justify-between gap-4"><div><div class="font-medium text-gray-900">Global notification block</div><p class="mt-1 text-sm text-gray-500">Stops all new notification generation across the app, including notification emails. Existing notifications remain visible.</p></div><label class="toggle-switch mt-1 shrink-0"><input type="checkbox" data-config-key="notificationFlowBlocked" ${tempConfigData.notificationFlowBlocked ? 'checked' : ''}><span class="slider"></span></label></div><div data-maintenance-lock-state="notificationFlowBlocked" class="mt-3 text-xs font-medium ${tempConfigData.notificationFlowBlocked ? 'text-amber-700' : 'text-gray-500'}">${tempConfigData.notificationFlowBlocked ? 'Blocked' : 'Active'}</div></div>
                    <div class="rounded-lg border border-gray-200 p-4"><div class="flex items-start justify-between gap-4"><div><div class="font-medium text-gray-900">Drugs and Leave action lock</div><p class="mt-1 text-sm text-gray-500">Frontend-only maintenance lock. Disables Drugs action controls and Leave request, edit, cancellation, and approval action controls.</p></div><label class="toggle-switch mt-1 shrink-0"><input type="checkbox" data-config-key="drugsUiLocked" ${tempConfigData.drugsUiLocked ? 'checked' : ''}><span class="slider"></span></label></div><div data-maintenance-lock-state="drugsUiLocked" class="mt-3 text-xs font-medium ${tempConfigData.drugsUiLocked ? 'text-amber-700' : 'text-gray-500'}">${tempConfigData.drugsUiLocked ? 'Locked' : 'Unlocked'}</div></div>
                    <div class="rounded-lg border border-gray-200 p-4"><div class="flex items-start justify-between gap-4"><div><div class="font-medium text-gray-900">Non-admin sign-in lock</div><p class="mt-1 text-sm text-gray-500">Signs out non-admin users and prevents them from remaining signed in while maintenance is active. Admin sessions are unaffected.</p></div><label class="toggle-switch mt-1 shrink-0"><input type="checkbox" data-config-key="nonAdminSignInLocked" ${tempConfigData.nonAdminSignInLocked ? 'checked' : ''}><span class="slider"></span></label></div><div data-maintenance-lock-state="nonAdminSignInLocked" class="mt-3 text-xs font-medium ${tempConfigData.nonAdminSignInLocked ? 'text-amber-700' : 'text-gray-500'}">${tempConfigData.nonAdminSignInLocked ? 'Locked' : 'Unlocked'}</div></div>
                    <div class="rounded-lg border border-gray-200 p-4 flex flex-col"><div><div class="font-medium text-gray-900">Log out all non-admin users</div><p class="mt-1 text-sm text-gray-500">One-shot maintenance action. Currently signed-in non-admin users are sent back to the login screen. Admins are not affected.</p></div><div class="mt-auto flex justify-end pt-4"><button id="trigger-non-admin-logout-btn" type="button" class="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700">Activate</button></div></div>
                </div>
            </div>
            <div class="bg-white p-6 rounded-lg shadow-md mb-8 max-w-5xl">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Configured options migration tool</h4>
                <p class="text-sm text-gray-600 mb-4">Batch update fields across the database. Select the "From" value and the "To" value to move items to a new status or urgency.<br><strong>Warning:</strong> This action will apply to active and closed items and cannot be undone.</p>
                <div class="mb-4"><label class="block text-sm font-medium text-gray-700 mb-1">Migration Type</label><div id="migrate-type-control"></div></div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4"><div><label class="block text-sm font-medium text-gray-700 mb-1">From (Old Value)</label><div id="migrate-old-val-control"></div></div><div><label class="block text-sm font-medium text-gray-700 mb-1">To (New Value)</label><div id="migrate-new-val-control"></div></div></div>
                <button id="run-migration-btn" class="rounded-lg bg-[var(--dfc-brand-primary)] hover:bg-[var(--dfc-brand-primary-hover)] text-[var(--dfc-brand-primary-contrast)] font-bold py-2 px-4">Run Migration</button>
            </div>
        </div>
    `;

    const migrateBtn = container.querySelector('#run-migration-btn');
    mountMigrationControls(container);

    if (migrateBtn) {
        migrateBtn.addEventListener('click', () => {
            const type = migrationSelection.type;
            const oldVal = migrationSelection.oldVal;
            const newVal = migrationSelection.newVal;
            const valueOptions = getMigrationValueOptions(type);
            const oldValText = findMigrationOptionLabel(valueOptions, oldVal);
            const newValText = findMigrationOptionLabel(valueOptions, newVal);
            const fieldName = getMigrationFieldLabel(type);
            const typeLabel = getMigrationTargetLabel(type);

            if (!oldVal || !newVal) {
                openConfirmationModal('Validation Error', "Select both a 'From' and 'To' value before running a migration.", async () => {}, false, 'OK');
                return;
            }

            if (oldVal === newVal) {
                openConfirmationModal('Validation Error', "The 'From' and 'To' values cannot be the same.", async () => {}, false, 'OK');
                return;
            }

            openConfirmationModal('Confirm Migration', `Are you sure you want to migrate ${fieldName} for all ${typeLabel} from <strong>'${escapeHTML(oldValText)}'</strong> to <strong>'${escapeHTML(newValText)}'</strong>?<br><br>This will update database records immediately and <strong>cannot be undone</strong>.`, async () => {
                migrateBtn.disabled = true;
                const originalText = migrateBtn.textContent;
                migrateBtn.textContent = "Processing...";
                migrateBtn.classList.add('opacity-50', 'cursor-not-allowed');
                try {
                    const result = await migrateData(type, oldVal, newVal);
                    migrateBtn.disabled = false;
                    migrateBtn.textContent = originalText;
                    migrateBtn.classList.remove('opacity-50', 'cursor-not-allowed');
                    setTimeout(() => {
                        openConfirmationModal('Migration Successful', `${result.count} items were updated.`, async () => {
                            window.location.reload();
                        }, false, 'OK');
                    }, 50);
                } catch (error) {
                    console.error(error);
                    migrateBtn.disabled = false;
                    migrateBtn.textContent = originalText;
                    migrateBtn.classList.remove('opacity-50', 'cursor-not-allowed');
                    setTimeout(() => {
                        openConfirmationModal('Migration Failed', error.message, async () => {}, false, 'OK');
                    }, 50);
                }
            }, true, 'Run Migration');
        });
    }

    bindStatusPrioritySettingsControls(container);
}

/**
 * Handles General tab button actions.
 *
 * @param {HTMLElement} target The clicked element.
 * @returns {Promise<boolean>} True when the click was handled.
 */
export async function handleGeneralSettingsButtonClick(target) {
    const deleteAssetBtn = target.closest("[data-branding-asset-delete]");
    if (deleteAssetBtn && !deleteAssetBtn.disabled) {
        const key = deleteAssetBtn.dataset.brandingAssetDelete;
        const label = key === "favicon" ? "favicon" : "logo";
        openConfirmationModal(
            `Delete ${label}`,
            `Delete the current ${label}? The app will use its default fallback.`,
            async () => {
                await handleBrandAssetDelete(
                    key,
                    () => renderGeneralTab(
                        document.getElementById("settings-tab-content"),
                    ),
                );
            },
            true,
            "Delete",
        );
        return true;
    }

    const triggerLogoutBtn = target.closest('#trigger-non-admin-logout-btn');
    if (!triggerLogoutBtn) {return false;}

    openConfirmationModal('Log Out Non-Admin Users', 'This will send currently signed-in non-admin users back to the login screen. Admin users will not be affected.', async () => {
        const logoutAt = new Date().toISOString();
        if (tempConfigData) {tempConfigData.nonAdminLogoutAt = logoutAt;}
        triggerLogoutBtn.disabled = true;
        try {
            await triggerNonAdminLogoutWave(logoutAt);
            openConfirmationModal('Maintenance Action Sent', 'Currently signed-in non-admin users have been instructed to log out.', () => {}, false, 'OK');
        } catch (error) {
            console.error('Failed to trigger non-admin logout wave:', error);
            openConfirmationModal('Error', error.message || 'Could not trigger the logout action.', () => {}, false, 'OK');
        } finally {
            triggerLogoutBtn.disabled = false;
        }
    }, true, 'Log Out Users');
    return true;
}

/**
 * Applies General tab config-input changes.
 *
 * @param {HTMLElement} target The event target.
 * @param {Function} rerender Callback to rerender the tab after changes.
 * @param {string} eventType Input event type.
 * @returns {boolean} True when the change was handled.
 */
export function handleGeneralSettingsConfigChange(target, rerender, eventType = "") {
    if (target.dataset.brandingAssetInput) {
        handleBrandAssetUpload(target, rerender);
        return true;
    }

    if (target.dataset.brandingKey) {
        const branding = ensureBrandingDraft();
        const key = target.dataset.brandingKey;

        if (key === "surfaceTintOpacity") {
            const value = Number(getSurfaceTintFromStrength(target.value).toFixed(2));
            branding.surfaceTintOpacity = value;
            syncBrandingControl(key, value);
            syncBrandingPreview();
            updateSaveButtonHandler(true);
            return true;
        }

        if (Object.hasOwn(DEFAULT_BRANDING, key)) {
            const rawValue = String(target.value || "").trim();
            const fallback = DEFAULT_BRANDING[key];
            if (
                target.dataset.brandingControl === "text" &&
                eventType === "input"
            ) {
                target.classList.toggle(
                    "app-colour-hex-input--invalid",
                    Boolean(rawValue) && !isHexColorInput(rawValue),
                );
                return true;
            }

            if (
                target.dataset.brandingControl === "text" &&
                rawValue &&
                !isHexColorInput(rawValue)
            ) {
                target.classList.add("app-colour-hex-input--invalid");
                return true;
            }

            const value = normalizeHexColor(rawValue, fallback);
            branding[key] = value;
            target.classList.remove("app-colour-hex-input--invalid");
            syncBrandingControl(key, value);
            syncBrandingPreview();
            updateSaveButtonHandler(true);
            return true;
        }
    }

    if (!(target.matches('input, select') && target.dataset.configKey)) {return false;}

    const configKey = target.dataset.configKey;
    tempConfigData[configKey] = target.type === 'checkbox'
        ? target.checked
        : target.type === 'number'
            ? (target.value === '' ? null : Number(target.value))
            : target.value || null;

    if (configKey === 'notificationFlowBlocked' && target.checked) {
        tempConfigData.drugsUiLocked = true;
    }

    updateSaveButtonHandler(true);
    if (target.type === "checkbox") {
        syncMaintenanceLockControls();
    } else {
        rerender?.();
    }
    return true;
}
